import { Grid } from '@material-ui/core'
import InputAdornment from '@material-ui/core/InputAdornment'
import { SECTOR_CHOICES } from 'constants/sectors'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import CustomField from 'elements/field/CustomField'
import ChipsInput from 'elements/input/ChipsInput'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { Box, Typography } from 'opensolar-ui'
import React, { FC, useState } from 'react'
import {
  BooleanInput,
  DateInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  useInput,
  useTranslate,
} from 'react-admin'
import { useField } from 'react-final-form'
import { useSelector } from 'react-redux'
import SoftWarning from 'resources/components/AdornmentWithWarning'
import { CHEAPER_HOME_BATTERIES_PROGRAM_INCENTIVE } from 'resources/incentives/IncentiveConstants'
import { maxBatterySizeWarning, maxSystemSizeWarning } from 'resources/incentives/validation'
import { doNotTranslate } from 'util/misc'
import { positiveOrZero } from 'validations'
import { StyledDateInput, StyledDateInputGridItem, StyledField, StyledPaper } from './styled'

// Normalizes various date input shapes into an API-friendly YYYY-MM-DD string or null
export const toApiDateOrNull = (value: unknown): string | null => {
  if (value === '' || value === undefined || value === null) return null
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'string') {
    // Assume already normalized or try to parse
    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? value : parsed.toISOString().slice(0, 10)
  }
  const parsed = new Date(value as any)
  return isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10)
}

const OneDecimalNumberInput = ({ source, label, ...props }) => {
  const {
    input: { value, onChange },
  } = useInput({ source, ...props })

  const [localValue, setLocalValue] = useState(value ?? '')

  const handleChange = (e) => {
    setLocalValue(e.target.value)
  }

  const handleBlur = () => {
    const floatVal = parseFloat(localValue)
    if (isNaN(floatVal)) {
      onChange(null)
      setLocalValue('')
    } else {
      // Round to 1 decimal
      const rounded = Math.round(floatVal * 10) / 10
      onChange(rounded)
      setLocalValue(rounded.toFixed(1))
    }
  }

  return (
    <NumberInput
      type="number"
      source={source}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      step="0.1"
      {...props}
    />
  )
}

type AutoApplyInputTypes = {
  enableByState?: boolean
  enableByZip?: boolean
  enableByUttility?: boolean
  enableByCountry?: boolean
  enableByComponentCode?: boolean
  enableByMinSystemSize?: boolean
  enableByMaxSystemSize?: boolean
  enableByMinBatterySize?: boolean
  enableByMaxBatterySize?: boolean
  enableBySector?: boolean
  enableByOrder?: boolean // This currently ONLY applies to incentive auto-apply
  enableByEndDate?: boolean
  enableByPriority?: boolean
  enableByRoofType?: boolean
  enableBySlope?: boolean
  disabled?: boolean
  isAdder?: boolean
  containerComponent?: React.ComponentType
}

const AutoApplyInputs: FC<AutoApplyInputTypes> = ({
  enableByState = true,
  enableByZip = true,
  enableByUttility = false,
  enableByCountry = false,
  enableByComponentCode = true,
  enableByMinSystemSize = true,
  enableByMaxSystemSize = true,
  enableByMinBatterySize = false,
  enableByMaxBatterySize = false,
  enableBySector = true,
  enableByOrder = false,
  enableByEndDate = false,
  enableByPriority = false,
  enableByRoofType = false,
  enableBySlope = false,
  disabled = false,
  isAdder = false,
  containerComponent = StyledPaper,
}) => {
  const translate = useTranslate()
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const countryId = useSelector(orgSelectors.getOrgCountryId)
  const incentiveType = useField('incentive_type', { subscription: { value: true } }).input.value
  const isCheaperHomeBatteries = incentiveType === CHEAPER_HOME_BATTERIES_PROGRAM_INCENTIVE

  const Comp = containerComponent

  return (
    <Comp>
      <Grid container spacing={3}>
        <Grid item xs={12} container alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="h6">{translate('Auto Apply Settings')}</Typography>
          </Grid>
        </Grid>
        {enableByState && (
          <Grid item xs={12} sm={12} lg={12}>
            <StyledField>
              <TextInput
                label={'Auto apply only in specified states'}
                name={'auto_apply_only_specified_states'}
                source="auto_apply_only_specified_states"
                disabled={disabled}
                endAdornment={
                  <InputAdornment position="end">
                    <Typography variant="body2">{translate('Comma separated values')}</Typography>
                  </InputAdornment>
                }
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByZip && (
          <Grid item xs={12} sm={12} lg={12}>
            <StyledField>
              <TextInput
                label={'Auto apply only in specified ZIP codes'}
                name={'auto_apply_only_specified_zips'}
                source="auto_apply_only_specified_zips"
                disabled={disabled}
                endAdornment={
                  <InputAdornment position="end">
                    <Typography variant="body2">{translate('Comma separated values')}</Typography>
                  </InputAdornment>
                }
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByUttility && (
          <Grid item xs={12} sm={12} lg={12}>
            <Box display="flex" alignItems="center" width="100%">
              <Box flexGrow={1}>
                <ReferenceArrayInput
                  name="auto_apply_only_specified_utilities"
                  source="auto_apply_only_specified_utilities"
                  reference="utilities"
                  filter={{ country: countryId }}
                  sort={{ field: 'name', order: 'ASC' }}
                  label={translate('Auto Apply') + ' ' + translate('Utility')}
                  fullWidth={true}
                  id="auto_apply_only_specified_utilities"
                  disabled={disabled}
                  onBlur={undefined}
                  onChange={undefined}
                  onFocus={undefined}
                  validate={undefined}
                  parse={(value) => value}
                  format={(value) => value}
                >
                  <ChipsInput optionText="name" optionValue="id" source="auto_apply_only_specified_utilities" />
                </ReferenceArrayInput>
              </Box>
              {enableByZip && (
                <Box ml={1}>
                  <InfoTooltip
                    placement="bottom-start"
                    title={translate(
                      'Specify one or more utility names for automatic adder application. If both utility name and zip code are set, the system will first try to match the utility name. If it matches, the adder will be applied. If not, it will check the zip code and apply the adder if there’s a match.'
                    )}
                    interactive
                  />
                </Box>
              )}
            </Box>
          </Grid>
        )}
        {enableByCountry && (
          <Grid item xs={12} sm={12} lg={12}>
            <StyledField>
              <TextInput
                label={'Auto apply only in specified countries (iso2)'}
                name={'auto_apply_only_specified_countries'}
                source="auto_apply_only_specified_countries"
                disabled={disabled}
                endAdornment={
                  <InputAdornment position="end">
                    <Typography variant="body2">{translate('Comma separated values')}</Typography>
                  </InputAdornment>
                }
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByComponentCode && (
          <Grid item xs={12} sm={12} lg={12}>
            <StyledField>
              <TextInput
                label={'Auto apply Component codes'}
                name={'auto_apply_component_codes'}
                source="auto_apply_component_codes"
                disabled={disabled}
                endAdornment={
                  <InputAdornment position="end">
                    <Typography variant="body2">{translate('Comma separated values')}</Typography>
                  </InputAdornment>
                }
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByEndDate && (
          <Grid item xs={12} sm={6} lg={4}>
            <StyledDateInputGridItem>
              <Box display="flex" alignItems="center" width="100%">
                <Box flexGrow={1}>
                  <StyledDateInput>
                    <DateInput
                      name={'auto_apply_end_date'}
                      source="auto_apply_end_date"
                      disabled={disabled}
                      fullWidth={true}
                      label={'Incentive Expiration'}
                      parse={toApiDateOrNull}
                    />
                  </StyledDateInput>
                </Box>
                <Box ml={1}>
                  <InfoTooltip
                    placement="right"
                    title={translate(
                      'Incentive will be actively removed from all unsold projects and will no longer be applied to new projects after this date.'
                    )}
                    interactive
                  />
                </Box>
              </Box>
            </StyledDateInputGridItem>
          </Grid>
        )}
        {(enableByMinSystemSize || enableByMaxSystemSize) && (
          <>
            <Grid item xs={12}>
              <Typography variant="body1SemiBold">{translate('Solar System Configuration')}</Typography>
            </Grid>
            {enableByMinSystemSize && (
              <Grid item xs={12} sm={4} lg={3}>
                <StyledField>
                  <NumberInput
                    label={'Auto apply min system size'}
                    name={'auto_apply_min_system_size'}
                    source="auto_apply_min_system_size"
                    disabled={disabled}
                    endAdornment={<InputAdornment position="end">kW</InputAdornment>}
                    validate={positiveOrZero}
                    fullWidth={true}
                  />
                </StyledField>
              </Grid>
            )}
            {enableByMaxSystemSize && (
              <Grid item xs={12} sm={4} lg={3}>
                <StyledField>
                  <NumberInput
                    label={'Auto apply max system size'}
                    name={'auto_apply_max_system_size'}
                    source="auto_apply_max_system_size"
                    disabled={disabled}
                    endAdornment={
                      <>
                        <SoftWarning
                          source="auto_apply_max_system_size"
                          validation={maxSystemSizeWarning(translate)}
                          adornment={'kW'}
                        />
                      </>
                    }
                    validate={positiveOrZero}
                    fullWidth={true}
                  />
                </StyledField>
              </Grid>
            )}
          </>
        )}
        {(enableByMinBatterySize || enableByMaxBatterySize) && (
          <>
            <Grid item xs={12}>
              <h4>{translate('Battery Configuration')}</h4>
              {isCheaperHomeBatteries && (
                <Typography variant="body2" color="textSecondary">
                  {doNotTranslate(
                    'Battery STCs are calculated using usable battery capacity. No capacity type selection is required.'
                  )}
                </Typography>
              )}
            </Grid>
            <Grid item container spacing={2} alignItems="flex-start">
              {enableByMinBatterySize && (
                <Grid item xs={12} sm={4} lg={3}>
                  <StyledField>
                    <NumberInput
                      label={'Min battery size'}
                      name={'auto_apply_min_battery_kwh'}
                      source="auto_apply_min_battery_kwh"
                      disabled={disabled}
                      endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                      validate={positiveOrZero}
                      fullWidth={true}
                    />
                  </StyledField>
                </Grid>
              )}
              {enableByMaxBatterySize && (
                <Grid item xs={12} sm={4} lg={3}>
                  <StyledField>
                    <NumberInput
                      label={'Max battery size'}
                      name={'auto_apply_max_battery_kwh'}
                      source="auto_apply_max_battery_kwh"
                      disabled={disabled}
                      endAdornment={
                        <>
                          <SoftWarning
                            source="auto_apply_max_battery_kwh"
                            validation={maxBatterySizeWarning(translate)}
                            adornment={'kWh'}
                          />
                        </>
                      }
                      validate={positiveOrZero}
                      fullWidth={true}
                    />
                  </StyledField>
                </Grid>
              )}
              {!isCheaperHomeBatteries && (
                <Grid item xs={12} sm={4} lg={3}>
                  <StyledField>
                    <SelectInput
                      label={'Capacity type'}
                      name={'auto_apply_battery_capacity_type'}
                      source="auto_apply_battery_capacity_type"
                      disabled={disabled}
                      choices={[
                        { id: '1', name: 'Nameplate' },
                        { id: '2', name: 'Usable' },
                      ]}
                      defaultValue="1"
                      optionText={(choice: any) => translate(choice?.name)}
                      optionValue="id"
                      fullWidth={true}
                    />
                  </StyledField>
                </Grid>
              )}
            </Grid>
          </>
        )}
        {enableBySector && (
          <Grid item xs={12} sm={4} lg={3}>
            <StyledField>
              <SelectInput
                label={'Auto apply sector'}
                name={'auto_apply_sector'}
                source="auto_apply_sector"
                disabled={disabled}
                choices={SECTOR_CHOICES}
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByPriority && (
          <Grid item xs={12} sm={4} lg={3}>
            <StyledField>
              <NumberInput
                label="Auto apply priority"
                name="auto_apply_priority"
                source="auto_apply_priority"
                disabled={disabled}
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {enableByRoofType && (
          <Grid item xs={12} sm={4} lg={3}>
            <StyledField>
              <ReferenceInput
                source="auto_apply_roof_type"
                name="auto_apply_roof_type"
                reference="roof_types"
                label={translate('Auto Apply') + ' ' + translate('Roof Type')}
                perPage={1000}
                fullWidth={true}
                id="auto_apply_roof_type"
                disabled={disabled}
                allowEmpty={true}
              >
                <SelectInput
                  optionText={(choice: any) => translate(choice?.name)}
                  optionValue="id"
                  id="roof_type_select_input"
                  source={'roof_type'}
                  fullWidth={true}
                />
              </ReferenceInput>
            </StyledField>
          </Grid>
        )}
        {enableBySlope && (
          <Grid container alignItems="center" spacing={2} style={{ paddingLeft: 12 }}>
            <Grid item xs={12} sm={4} lg={3}>
              <StyledField>
                <OneDecimalNumberInput
                  label={'Auto apply min slope'}
                  name={'auto_apply_min_slope'}
                  source="auto_apply_min_slope"
                  disabled={disabled}
                  endAdornment={<InputAdornment position="end">{'\u00B0'}</InputAdornment>}
                  fullWidth
                  style={{ width: '100%' }}
                />
              </StyledField>
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
              <StyledField>
                <OneDecimalNumberInput
                  label={'Auto apply max slope'}
                  name={'auto_apply_max_slope'}
                  source="auto_apply_max_slope"
                  disabled={disabled}
                  endAdornment={<InputAdornment position="end">{'\u00B0'}</InputAdornment>}
                  fullWidth
                  style={{ width: '100%' }}
                />
              </StyledField>
            </Grid>
            <Grid item>
              <InfoTooltip
                placement="bottom-start"
                title={translate(
                  'Automatically apply adders based on roof slope. Define a minimum and maximum slope (degrees) for application. If maximum is blank, the adder applies if the slope is greater than or equal to the minimum. Adders are applied per panel group; thus, a system may receive multiple adders if multiple groups meet the criteria. This adder’s quantity is automatically calculated based on the number of panels that match, so it cannot be manually changed.'
                )}
                interactive
              />
            </Grid>
          </Grid>
        )}
        {enableByOrder && (
          <Grid item xs={12} sm={4} lg={3}>
            <StyledField>
              <SelectInput
                label="Display order"
                name={'order'}
                source="order"
                choices={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => {
                  return { id: value, name: value }
                })}
                disabled={disabled}
                fullWidth={true}
              />
            </StyledField>
          </Grid>
        )}
        {isAdder && isAdmin && (
          <Grid item xs={12} sm={12} lg={12}>
            <CustomField
              style={{ width: '100%' }}
              name="only_admin_can_remove"
              label="Only Admins can remove"
              defaultValue={false}
              component={BooleanInput}
            />
          </Grid>
        )}
      </Grid>
    </Comp>
  )
}

export default AutoApplyInputs
